<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';
import api from 'flow_builder/api/index.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const arrow = '→';

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));
const flowList = inject('flowList', ref([]));

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  return [...base, ...(flowDynamicVariables?.value ?? [])];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const flows = ref([]);

onMounted(async () => {
  if (flowList.value && flowList.value.length) {
    flows.value = flowList.value;
  } else {
    try {
      const { data } = await api.getFlows();
      flows.value = data.flows ?? [];
    } catch {
      flows.value = [];
    }
  }
});

const flowOptions = computed(() => flows.value.map(f => ({ id: f.id, name: f.name })));
const selectedFlow = computed(() => flowOptions.value.find(f => f.id === props.modelValue?.flow_id) ?? null);

function onSelectFlow(option) {
  update({ flow_id: option?.id ?? null, flow_name: option?.name ?? '' });
}

const inputMappings = computed(() => props.modelValue?.input_mappings ?? []);
const outputMappings = computed(() => props.modelValue?.output_mappings ?? []);

function addInputMapping() {
  update({ input_mappings: [...inputMappings.value, { source: '', target: '' }] });
}
function removeInputMapping(i) {
  update({ input_mappings: inputMappings.value.filter((_, idx) => idx !== i) });
}
function updateInputMapping(i, patch) {
  update({ input_mappings: inputMappings.value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) });
}

function addOutputMapping() {
  update({ output_mappings: [...outputMappings.value, { source: '', target: '' }] });
}
function removeOutputMapping(i) {
  update({ output_mappings: outputMappings.value.filter((_, idx) => idx !== i) });
}
function updateOutputMapping(i, patch) {
  update({ output_mappings: outputMappings.value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) });
}

// Variable picker
const openPickerKey = ref(null);
const varSearch = ref('');

const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q) || v.key.toLowerCase().includes(q));
});

function togglePicker(key) {
  openPickerKey.value = openPickerKey.value === key ? null : key;
  varSearch.value = '';
}

function closePicker() {
  openPickerKey.value = null;
  varSearch.value = '';
}

function selectVar(varKey, type, i, field) {
  if (type === 'input') updateInputMapping(i, { [field]: `{{${varKey}}}` });
  closePicker();
}
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Fluxo -->
    <div class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.FLOW') }}</span>
      <MultiselectDropdown
        :options="flowOptions"
        :selected-item="selectedFlow"
        :multiselector-placeholder="t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.SELECT_FLOW')"
        :input-placeholder="t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.SEARCH_FLOW')"
        :has-thumbnail="false"
        :hide-search="false"
        @select="onSelectFlow"
      />
    </div>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Variáveis de entrada -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.INPUT_VARIABLES') }}</span>

      <div v-for="(m, i) in inputMappings" :key="i" class="flex items-center gap-1.5">
        <!-- source: aceita variável -->
        <div v-on-click-outside="closePicker" class="relative flex-1 min-w-0">
          <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
            <input
              class="reset-base flex-1 min-w-0 h-8 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
              :value="m.source"
              :placeholder="t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.SOURCE_PLACEHOLDER')"
              @input="updateInputMapping(i, { source: $event.target.value })"
            />
            <button
              type="button"
              class="shrink-0 inline-flex items-center justify-center size-5 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
              :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
              @click.stop="togglePicker(`input_${i}`)"
            >
              <span class="i-ph-brackets-curly size-3 flex-shrink-0" />
            </button>
          </div>
          <div
            v-if="openPickerKey === `input_${i}`"
            class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
          >
            <div class="px-2 pt-2 pb-1">
              <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
                <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
                <input v-model="varSearch" type="text" class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9" :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')" autofocus />
              </div>
            </div>
            <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
              <button v-for="v in filteredVars" :key="v.key" type="button" class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors" @click="selectVar(v.key, 'input', i, 'source')">{{ resolveLabel(v.label) }}</button>
              <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
            </div>
          </div>
        </div>
        <span class="text-xs text-n-slate-9 shrink-0">{{ arrow }}</span>
        <!-- target: nome simples -->
        <input
          class="flex-1 min-w-0 reset-base px-2 py-1.5 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-xs text-n-slate-12 placeholder:text-n-slate-9"
          :value="m.target"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.TARGET_PLACEHOLDER')"
          @input="updateInputMapping(i, { target: $event.target.value })"
        />
        <button type="button" class="i-lucide-trash-2 size-4 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors" @click="removeInputMapping(i)" />
      </div>

      <Button ghost slate sm icon="i-lucide-plus" :label="t('FLOW_BUILDER.COMMON.ADD')" class="self-start" @click="addInputMapping" />
    </div>

    <div class="h-px bg-n-weak -mx-4" />

    <!-- Variáveis de saída -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.OUTPUT_VARIABLES') }}</span>

      <div v-for="(m, i) in outputMappings" :key="i" class="flex items-center gap-1.5">
        <input
          class="flex-1 min-w-0 reset-base px-2 py-1.5 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-xs text-n-slate-12 placeholder:text-n-slate-9"
          :value="m.source"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.OUTPUT_SOURCE_PLACEHOLDER')"
          @input="updateOutputMapping(i, { source: $event.target.value })"
        />
        <span class="text-xs text-n-slate-9 shrink-0">{{ arrow }}</span>
        <input
          class="flex-1 min-w-0 reset-base px-2 py-1.5 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-xs text-n-slate-12 placeholder:text-n-slate-9"
          :value="m.target"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.CALL_FLOW.OUTPUT_TARGET_PLACEHOLDER')"
          @input="updateOutputMapping(i, { target: $event.target.value })"
        />
        <button type="button" class="i-lucide-trash-2 size-4 text-n-slate-9 hover:text-n-ruby-11 shrink-0 transition-colors" @click="removeOutputMapping(i)" />
      </div>

      <Button ghost slate sm icon="i-lucide-plus" :label="t('FLOW_BUILDER.COMMON.ADD')" class="self-start" @click="addOutputMapping" />
    </div>

  </div>
</template>
